import { useInfiniteQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

const log = createLogger('useSchoolStudentsQuery');

export const SCHOOL_STUDENTS_QUERY_KEY = 'schoolStudents';

export type SchoolStudent = {
  studentId: string;
  voiceName: string;
  periodId: string;
  periodName: string;
  periodShortName: string;
  teacherName: string;
  slug: string;
};

/** Fields the backend can sort the school-students list by. Kept narrow on
 *  purpose — adding one requires a backend change as well. */
export type SchoolStudentsSortBy = 'voiceName' | 'periodName' | 'periodShortName' | 'teacherName';
export type SortDirection = 'asc' | 'desc';

export type SchoolStudentsResponse = {
  students: SchoolStudent[];
  nextCursor?: number;
};

export type UseSchoolStudentsQueryParams = {
  sortBy?: SchoolStudentsSortBy;
  sortDirection?: SortDirection;
};

export function useSchoolStudentsQuery({ sortBy, sortDirection }: UseSchoolStudentsQueryParams = {}) {
  const httpClient = useHttpClient();

  const { data, error, status, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery<
    SchoolStudentsResponse,
    Error,
    { pages: SchoolStudentsResponse[]; pageParams: number[] },
    [string, string | null, string | null],
    number
  >({
    queryKey: [SCHOOL_STUDENTS_QUERY_KEY, sortBy ?? null, sortDirection ?? null],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam > 0) params.set('cursor', String(pageParam));
      if (sortBy) params.set('sortBy', sortBy);
      if (sortDirection) params.set('sortDirection', sortDirection);

      const queryString = params.toString();
      const url = queryString ? `/student/getSchoolStudents?${queryString}` : '/student/getSchoolStudents';

      log.debug('Fetching school students', { sortBy, sortDirection, cursor: pageParam });
      const response = await httpClient.get<SchoolStudentsResponse>(url);
      log.info('Fetched school students page', {
        studentCount: response.students.length,
        hasMore: response.nextCursor != null,
      });
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch school students', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status, fetchNextPage, isFetchingNextPage, hasNextPage };
}
